import { Injectable, inject } from '@angular/core';
import { RestService } from '@copec-gid/gid-arquitectura.seguridad-autorizacion-modulo';
import { GidLoggerService } from '@copec-gid/gid-arquitectura.console-log';
import { lastValueFrom, Observable, ReplaySubject, timeout } from 'rxjs';
import { ErrorType } from '../enums/enums.enum';
import { CustomErrorHandler } from './error-handler.service';
import { Globals } from '../globals';
import { PreAuthenticationDto } from '../persistence/dto/pre-authentication-dto';
import { AuthenticationDto } from '../persistence/dto/authentication-dto';
import { PostAuthenticationDto } from '../persistence/dto/post-authentication-dto';
import { EnrollDto } from '../persistence/dto/enroll-dto';

import { PreAuthenticationConverter } from '../persistence/converter/pre-authentication-converter';
import { AuthenticationConverter } from '../persistence/converter/authentication-converter';
import { PostAuthenticationConverter } from '../persistence/converter/post-authentication-converter';
import { EnrollConverter } from '../persistence/converter/enroll-converter';

import { PreAuthenticationModel } from '../models/pre-authentication-model';
import { AuthenticationModel } from '../models/authentication-model';
import { PostAuthenticationModel } from '../models/post-authentication-model';
import { EnrollModel } from '../models/enroll-model';
import { EndpointResource } from '../core/conf/endpoint_conf';
import { LIB_HEADERS } from '../core/conf/lib_conf';


@Injectable({
  providedIn: 'root'
})
export class TokenizationService {

  private _authStatus: ReplaySubject<any> = new ReplaySubject<any>();
  public readonly authStatus$: Observable<any> = this._authStatus.asObservable();

  private _dataCollectionReady: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  public readonly dataCollectionReady$: Observable<boolean> = this._dataCollectionReady.asObservable();

  medioPagoId: string;

	successMessage: { title: string; subtitle: string; } = { title: "", subtitle: "" };
	setSuccessMessageFromEnrollResponse(res: EnrollModel) {
		this.successMessage = {
			title: res.userMessageTitle,
			subtitle: res.userMessageDesc
		};
	}
  
  updateAuthStatusData(newData: any) {
    this._authStatus.next(newData);
  }
  
  updateDataCollectionReady() {
    this._dataCollectionReady.next(true);
  }
  
  private readonly rest = inject(RestService);

  constructor(
    private errorHandler: CustomErrorHandler,
    private logger: GidLoggerService
  ) { }

  async call3DSPreAuthentication(cardData: any): Promise<PreAuthenticationModel|null> {
    let preAuthResponse: any;
    try {      
      preAuthResponse = await lastValueFrom(this.rest.post<any>(
        EndpointResource.preAuthEndpoint,
        {
          "origen": "COPEC_APP",
          "medio_pago_id": this.medioPagoId,
          "tarjeta": {
            "detalle_tarjeta": {
              "numero_tarjeta": cardData.number,
              "mes_exp": cardData.expiry_month,
              "ano_exp": cardData.expiry_year,
              "cvv": cardData.cvc
            }
          }
        },
        { headers: LIB_HEADERS })
        .pipe(timeout(Globals.apiTimeout))
      );
    } catch (error) {
      this.logger.error('call3DSPreAuthentication', Globals.sanitizeForLog(error));

      if (this.errorHandler.isTimeoutError(error)) {
        this.errorHandler.reportError(error, ErrorType.TIMEOUT);
      } else {
        this.errorHandler.reportError(error, ErrorType.PRE_AUTH);
      }
      
      return null;
    }

    if (!preAuthResponse) {
      this.logger.error('call3DSPreAuthentication empty data', Globals.sanitizeForLog(preAuthResponse));
      this.errorHandler.reportError(preAuthResponse, ErrorType.PRE_AUTH);
      return null;
    }

    if (preAuthResponse.status && preAuthResponse.status !== 200) {
      this.logger.error('call3DSPreAuthentication status not 200', Globals.sanitizeForLog(preAuthResponse));
      this.errorHandler.reportError(preAuthResponse, ErrorType.PRE_AUTH);
      return null;
    }
    
    if (preAuthResponse.status_code && preAuthResponse.status_code !== 'S00') {
      this.logger.error('call3DSPreAuthentication status_code not S00', Globals.sanitizeForLog(preAuthResponse));
      this.errorHandler.reportError(preAuthResponse, ErrorType.PRE_AUTH);
      return null;
    }
    try {
      const preAuthDto = new PreAuthenticationDto(preAuthResponse);
      return PreAuthenticationConverter.convert(preAuthDto);
    } catch (error) {
      this.logger.error('call3DSPreAuthentication convert error', Globals.sanitizeForLog(error));
      this.errorHandler.reportError(error, ErrorType.DATA_CONVERT);
      return null;
    }
  }

  async call3DSAuthentication(): Promise<AuthenticationModel|null> {
    let authResponse: any;
    try {
      authResponse = await lastValueFrom(this.rest.post<any>(
        EndpointResource.authEndpoint,
        { "origen": "COPEC_APP", "medio_pago_id": this.medioPagoId },
        { headers: LIB_HEADERS })
        .pipe(timeout(Globals.apiTimeout))
      );
    } catch (error) {
      this.logger.error('call3DSAuthentication', Globals.sanitizeForLog(error));

      if (this.errorHandler.isTimeoutError(error)) {
        this.errorHandler.reportError(error, ErrorType.TIMEOUT);
      } else {
        this.errorHandler.reportError(error, ErrorType.AUTH);
      }
      
      return null;
    }

    if (!authResponse) {
      this.logger.error('call3DSAuthentication empty data', Globals.sanitizeForLog(authResponse));
      this.errorHandler.reportError(authResponse, ErrorType.AUTH);
      return null;
    }

    if (authResponse.status && authResponse.status !== 200) {
      this.logger.error('call3DSAuthentication status not 200', Globals.sanitizeForLog(authResponse));
      this.errorHandler.reportError(authResponse, ErrorType.AUTH);
      return null;
    }
    
    if (
      authResponse.status_code && 
      authResponse.status_code !== 'S00' && 
      authResponse.status_code !== 'S01'
    ) {
      this.logger.error('call3DSAuthentication status_code not S00 or S01', Globals.sanitizeForLog(authResponse));
      this.errorHandler.reportError(authResponse, ErrorType.AUTH);
      return null;
    }

    try {
      const authDto = new AuthenticationDto(authResponse);
      return AuthenticationConverter.convert(authDto);
    } catch (error) {
      this.logger.error('call3DSAuthentication convert error', Globals.sanitizeForLog(error));
      this.errorHandler.reportError(error, ErrorType.DATA_CONVERT);
      return null;
    }
  }

  async call3DSPostAuthorization(): Promise<PostAuthenticationModel|null> {
    let postAuthResponse: any;
    try {
      postAuthResponse = await lastValueFrom(this.rest.post<any>(
        EndpointResource.postAuthEndpoint,
        { "origen": "COPEC_APP", "medio_pago_id": this.medioPagoId },
        { headers: LIB_HEADERS })
        .pipe(timeout(Globals.apiTimeout))
      );
    } catch (error) {
      this.logger.error('call3DSPostAuthentication', Globals.sanitizeForLog(error));

      if (this.errorHandler.isTimeoutError(error)) {
        this.errorHandler.reportError(error, ErrorType.TIMEOUT);
      } else {
        this.errorHandler.reportError(error, ErrorType.POST_AUTH);
      }

      return null;
    } 

    if (!postAuthResponse) {
      this.logger.error('call3DSPostAuthentication empty data', Globals.sanitizeForLog(postAuthResponse));
      this.errorHandler.reportError(postAuthResponse, ErrorType.POST_AUTH);
      return null;
    }

    if (postAuthResponse.status && postAuthResponse.status !== 200) {
      this.logger.error('call3DSPostAuthentication status not 200', Globals.sanitizeForLog(postAuthResponse));
      this.errorHandler.reportError(postAuthResponse, ErrorType.POST_AUTH);
      return null;
    }
    
    if (postAuthResponse.status_code && postAuthResponse.status_code !== 'S00') {
      this.logger.error('call3DSPostAuthentication status_code not S00', Globals.sanitizeForLog(postAuthResponse));
      this.errorHandler.reportError(postAuthResponse, ErrorType.POST_AUTH);
      return null;
    }

    try {
      const postAuthDto = new PostAuthenticationDto(postAuthResponse);
      return PostAuthenticationConverter.convert(postAuthDto);
    } catch (error) {
      this.logger.error('call3DSPostAuthentication convert error', Globals.sanitizeForLog(error));
      this.errorHandler.reportError(error, ErrorType.DATA_CONVERT);
      return null;
    }
  }

  async callEnrollCard(): Promise<EnrollModel|null> {
    let enrollResponse: any;
    try {
      enrollResponse = await lastValueFrom(this.rest.post<any>(
        EndpointResource.enrollEndpoint,
        { "origen": "COPEC_APP", "medio_pago_id": this.medioPagoId },
        { headers: LIB_HEADERS })
        .pipe(timeout(Globals.apiTimeout))
      );
    } catch (error) {
      this.logger.error('callEnrollCard', Globals.sanitizeForLog(error));

      if (this.errorHandler.isTimeoutError(error)) {
        this.errorHandler.reportError(error, ErrorType.TIMEOUT);
      } else {
        this.errorHandler.reportError(error, ErrorType.ENROLL);
      }

      return null;
    }
    
    if (!enrollResponse) {
      this.logger.error('callEnrollCard empty data', Globals.sanitizeForLog(enrollResponse));
      this.errorHandler.reportError(enrollResponse, ErrorType.ENROLL);
      return null;
    }

    if (enrollResponse.status && enrollResponse.status !== 200) {
      this.logger.error('callEnrollCard status not 200', Globals.sanitizeForLog(enrollResponse));
      this.errorHandler.reportError(enrollResponse, ErrorType.ENROLL);
      return null;
    }
    
    if (enrollResponse.status_code && enrollResponse.status_code !== 'S00') {
      this.logger.error('callEnrollCard status_code not S00', Globals.sanitizeForLog(enrollResponse));
      this.errorHandler.reportError(enrollResponse, ErrorType.ENROLL);
      return null;
    }

    try {
      const enrollDto = new EnrollDto(enrollResponse);
      return EnrollConverter.convert(enrollDto);
    } catch (error) {
      this.logger.error('callEnrollCard convert error', Globals.sanitizeForLog(error));
      this.errorHandler.reportError(error, ErrorType.DATA_CONVERT);
      return null;
    }
  }

}



